/**
 * @file Renders the tabbed detail view of one personal access token.
 */
import React, { useState } from "react";
import { Row, Col, Tabs, Alert, Descriptions, Typography } from "antd";

import { W0, W1 } from "@components/Constants";
import { CodeBlock } from "@components/Text";
import { getConfig } from "@components/Config";
import { useAppContext } from "@components/AppContext";
import { getFormattedIsoDateTime } from "@components/Date";
import { type AppContext } from "@components/AppContext/types";
import { type PersonalAccessToken } from "@api/types";

import PersonalAccessTokenSpecification from "./PersonalAccessTokenSpecification";
import getApiSandboxUrl from "./helpers/getApiSandboxUrl";
import getSpecification from "./helpers/getSpecification";
import getAccessTokenSnippet from "./helpers/getAccessTokenSnippet";

const LABEL_TOKEN_ID = "Token ID";
const LABEL_OWNER = "Owner";
const LABEL_TOKEN = "Token";
const LABEL_SNIPPET = "Use the token";
const LABEL_SPECIFICATION = "Specification";

const LABEL_AUDIENCES = "Audiences";
const LABEL_ALL_AUDIENCES = "All";
const LABEL_EXPIRES_AT = "Expires";
const LABEL_CREATED_AT = "Created";
const LABEL_LAST_USED_AT = "Last used";
const LABEL_LAST_USED_IP = "Last used from";
const LABEL_USAGE_COUNT = "Uses";
const LABEL_FAILED_ATTEMPT_COUNT = "Failed exchanges";
const LABEL_EMPTY = "-";

const DESCRIPTION_COMMON = "The token carries your own access, therefore each call of the client acts as you. The platform gives the secret one time, at the creation of the token. Make a new token when you lose that secret.";

const DESCRIPTION_CONSOLE = "The client changes the secret into the authentication token, and it then opens the session of one organization of the console.";

const DESCRIPTION_BACKSTAGE = "The client changes the secret into the authentication token, and it then opens the session of your organization. Give the client the organization ID below together with the secret.";

const LABEL_REVOKED = "This token is revoked";
const LABEL_EXPIRED = "This token is expired";
const DESCRIPTION_STOPPED = "The platform refuses the secret of this token now. Make a new token for each client that uses it.";

const COLUMN_SPAN = 24;
const DESCRIPTIONS_COLUMN_COUNT = 2;

// NOTE: the snippet is the tallest block of the modal. The value keeps the
//       block inside the window, and it counts the title, the two values
//       above the tabs and the tab bar.
const SNIPPET_MAX_HEIGHT = "calc(100vh - 340px)";

const STATUS_ACTIVE = "ACTIVE";

const TAB_TOKEN = "token";
const TAB_SNIPPET = "snippet";
const TAB_SPECIFICATION = "specification";

type Props = {
  personalAccessToken: PersonalAccessToken;
};

/**
 * Renders the token, snippet and specification tabs for one access token.
 */
const PersonalAccessTokenDetails = ({
  personalAccessToken,
}: Props): JSX.Element => {
  const {
    identity,
    hasConsoleAccess,
    getOrganizationId,
  } = useAppContext() as AppContext;

  const [ activeTabKey, setActiveTabKey ] = useState(TAB_TOKEN);

  const {
    id,
    email,
    status,
    audiences,
    createdAt,
    expiresAt,
    usageCount,
    lastUsedAt,
    lastUsedIp,
    failedAttemptCount,
  } = personalAccessToken;

  // NOTE: an operator of the console reaches more than one organization,
  //       therefore the token of that person reaches the console API. The
  //       token of each other operator reaches the API of the back office.
  const isConsoleOperator = hasConsoleAccess === true;

  const description = isConsoleOperator
    ? DESCRIPTION_CONSOLE
    : DESCRIPTION_BACKSTAGE;

  const domain = identity?.organization?.domain;
  const organizationId = getOrganizationId();
  const consoleOrganizationId = getConfig("consoleOrganizationId") as string;

  const specification = getSpecification({
    domain,
    isConsoleOperator,
  });

  const snippet = getAccessTokenSnippet({
    host: specification.host,
    organizationId,
    isConsoleOperator,
    consoleOrganizationId,
  });

  const sandboxUrl = getApiSandboxUrl({
    organizationId,
    isConsoleOperator,
  });

  const isRevoked = status !== STATUS_ACTIVE;
  const isExpired = new Date(expiresAt).getTime() < Date.now();
  const isStopped = isRevoked || isExpired;

  const stoppedMessage = isRevoked
    ? LABEL_REVOKED
    : LABEL_EXPIRED;

  const hasAudiences = !!audiences && audiences.length > 0;

  const audiencesValue = hasAudiences
    ? audiences.join(", ")
    : LABEL_ALL_AUDIENCES;

  const lastUsedIpValue = lastUsedIp || LABEL_EMPTY;

  const labelStyle = {
    marginBottom: W0 / 2,
  };

  const valueStyle = {
    marginBottom: W1,
    wordBreak: "break-all" as const,
  };

  /**
   * Renders one labeled, copyable value in the token tab.
   */
  const renderValue = (label: string, value: string) => (
    <Col span={COLUMN_SPAN}>
      <Typography.Paragraph type="secondary" style={labelStyle}>
        {label}
      </Typography.Paragraph>

      <Typography.Paragraph
        code
        copyable={{ text: value }}
        style={valueStyle}
      >
        {value}
      </Typography.Paragraph>
    </Col>
  );

  // NOTE: the Token tab holds text and values only, and each value of the
  //       Specification tab has a copy control of its own. Therefore the
  //       control of the tab bar is there for the snippet only.
  const codeByTabKey: Record<string, string | undefined> = {
    [TAB_SNIPPET]: snippet,
  };

  const activeCode = codeByTabKey[activeTabKey];

  const hasActiveCode = !!activeCode;

  const tokenTabChildren = (
    <>
      <Typography.Paragraph type="secondary">
        {DESCRIPTION_COMMON}

        {" "}

        {description}
      </Typography.Paragraph>

      <Row gutter={W1}>
        {renderValue(LABEL_TOKEN_ID, id)}
        {renderValue(LABEL_OWNER, email)}
      </Row>

      <Descriptions
        bordered
        size="small"
        column={DESCRIPTIONS_COLUMN_COUNT}
      >
        <Descriptions.Item label={LABEL_AUDIENCES}>
          {audiencesValue}
        </Descriptions.Item>

        <Descriptions.Item label={LABEL_EXPIRES_AT}>
          {getFormattedIsoDateTime(expiresAt)}
        </Descriptions.Item>

        <Descriptions.Item label={LABEL_CREATED_AT}>
          {getFormattedIsoDateTime(createdAt)}
        </Descriptions.Item>

        <Descriptions.Item label={LABEL_LAST_USED_AT}>
          {getFormattedIsoDateTime(lastUsedAt)}
        </Descriptions.Item>

        <Descriptions.Item label={LABEL_LAST_USED_IP}>
          {lastUsedIpValue}
        </Descriptions.Item>

        <Descriptions.Item label={LABEL_USAGE_COUNT}>
          {usageCount}
        </Descriptions.Item>

        <Descriptions.Item label={LABEL_FAILED_ATTEMPT_COUNT}>
          {failedAttemptCount}
        </Descriptions.Item>
      </Descriptions>
    </>
  );

  const tabItems = [
    {
      key: TAB_TOKEN,
      label: LABEL_TOKEN,
      children: tokenTabChildren,
    },
    {
      key: TAB_SNIPPET,
      label: LABEL_SNIPPET,
      children: (
        <CodeBlock
          code={snippet}
          maxHeight={SNIPPET_MAX_HEIGHT}
        />
      ),
    },
    {
      key: TAB_SPECIFICATION,
      label: LABEL_SPECIFICATION,
      children: (
        <PersonalAccessTokenSpecification
          sandboxUrl={sandboxUrl}
          specification={specification}
          organizationId={organizationId}
          isConsoleOperator={isConsoleOperator}
          consoleOrganizationId={consoleOrganizationId}
        />
      ),
    },
  ];

  // NOTE: the copy control keeps the copied state of the tab that it was on,
  //       therefore the key makes a new control for each tab.
  const tabBarExtraContent = {
    right: hasActiveCode
      ? (
        <Typography.Text
          key={activeTabKey}
          copyable={{ text: activeCode }}
        />
      )
      : null,
  };

  return (
    <>
      {isStopped && (
        <Alert
          showIcon
          type="warning"
          message={stoppedMessage}
          description={DESCRIPTION_STOPPED}
          style={{ marginBottom: W1 }}
        />
      )}

      <Tabs
        size="small"
        items={tabItems}
        activeKey={activeTabKey}
        tabBarExtraContent={tabBarExtraContent}
        onChange={setActiveTabKey}
      />
    </>
  );
};

export default PersonalAccessTokenDetails;
